/**
 * Tier gating helpers.
 *
 * Fail-closed policy: if user is null or data is missing, features default to LOCKED.
 * This is critical for billing — never grant Pro features when state is uncertain.
 *
 * ONE SOURCE OF TRUTH: the server. `/api/me` returns the tier the backend ACTS ON (an admin's
 * `acting_tier` if they have opted into one, else their real tier) together with the
 * capabilities resolved from it, so the client just reports what the server already decided.
 *
 * There used to be a second, client-only lens: an `is_sim_tier` localStorage value that this
 * module layered over the server's answer. The backend had never heard of it, so an admin
 * "simulating" Free got a Free-looking UI in front of a server that still granted them
 * everything — the simulation proved nothing, and any test resting on it was not exercising
 * the real gate. Simulation is now a server-side `acting_tier`; see `PATCH /api/me/acting-tier`.
 */

import type { User } from '../auth/AuthContext';
import { TIER, TierValue } from '../constants/tiers';

/** The tier the UI should treat the user as — exactly the one the server is acting on. */
export function effectiveTier(user: User | null): TierValue {
  return user?.tier ?? TIER.ANONYMOUSE;
}

/**
 * Should the UI present this session as signed OUT?
 *
 * True for a real visitor, and for an admin acting as Guest — where the server already
 * withholds the account (Services\CurrentUser), so showing "My Account" over an API that returns
 * a guest's view is the mismatch this exists to prevent. The flag is read from the server rather
 * than derived from the tier, because /api/me still ships `is_admin` so the picker stays reachable.
 */
export function presentsAsSignedOut(user: User | null): boolean {
  return !user || user.acting_anonymously === true;
}

export function isProUser(user: User | null): boolean {
  return effectiveTier(user) === TIER.PRO;
}

export function canUseFeature(user: User | null, feature: keyof User['features']): boolean {
  // Fail closed on a MISSING map too, not just a missing user. `features` is always present on
  // a real `/api/me` response, but "data is missing" is exactly when this must not throw — an
  // exception here takes the whole editor down rather than locking one button.
  if (!user?.features) return false;
  return Boolean(user.features[feature]);
}
